/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2022, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/

import type { FeatureFlags } from './feature-flags';
import type { PlanMetering, PlanName } from './plan';
import type { InstancePreferences, UserPreferences } from './preferences';
import type { Role } from '.';

export type InstanceState = {
  plan: PlanName;
  state: string;
};

export type Instance = {
  instance_id: string;
  crn: string;
  name: string;
  region: string;
  plan: {
    guid: string;
    name: string;
  };
  account?: {
    id: string;
    name: string;
  };
  resourceGroup?: {
    id: string;
    name: string;
  };
};

export type ToolingData = Record<string, any>;

export type Instances = {
  resources: Instance[];
  inaccessibleAccountNames: string[];
};

export type InstanceData = {
  plan: Plan;
  limits: InstanceLimits;
  featureFlags: FeatureFlags;
  instance: InstanceInfo;
  instance_role: Role;
  instance_custom_roles: string[];
  tooling_data: ToolingData;
  new_ia: boolean;
  user_preferences: UserPreferences;
  instance_preferences: InstancePreferences;
  conversational_skill_provider?: ConvoSkillProviderInfo;
};

export type Plan = {
  plan_id: string;
  name: PlanName;
  metering?: PlanMetering;
};

export type InstanceLimits = {
  steps: number;
  skills: number;
  values: number;
  actions: number;
  intents: number;
  entities: number;
  examples: number;
  environments: number;
  handlers: number;
  mentions: number;
  synonyms: number;
  notifiers: number;
  snapshots: number;
  variables: number;
  assistants: number;
  processors: number;
  workspaces: number;
  max_timeout: number;
  dialog_nodes: number;
  open_entities: number;
  counterexamples: number;
  default_timeout: number;
  skill_references: number;
  concurrent_sessions: number;
  action_skill_references: number;
  dialog_skill_references: number;
  search_skill_references: number;
  external_skill_references: number;
  orchestrator_skill_references: number;
  data_types: number;
  catalog_integrations: number;
};

export type InstanceInfo = {
  crn_id: string;
  region: string;
  account_id: string;
  organization_id: any;
  name: string;
  resource_group_id: string;
};

export type ConvoSkillProviderInfo = {
  provider_id: string;
  conversational_skill_provider_spec_hash_id?: string;
};

/**
 * Query parameters that, when used on the Tooling URL, will be sent to the
 * server when querying for instance information.
 */
export const enum GetInstanceQueryParam {
  // The value is the literal query parameter the user may specify at the end
  // of the Tooling URL to force the server to serve Classic IA Tooling and
  // reset the user preference to be Classic IA.
  FORCE_CLASSIC = 'forceClassic'
}
